import {
  LoaderFunction,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { formatToSimpleDate } from "@/utils/dayjsUtils";
import { get } from "api";
import { Models, OrderDeliveryStatus, OrderPickupStatus } from "api/types";
import { Button } from "atoms/button/Button";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import {
  getDeliveryProgress,
  getPickupProgress,
  RetailProductDeliverySection,
  RetailProductOrderActions,
  RetailProductOrderHeader,
  RetailProductPickupSection,
} from "modules/retailProducts";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";

const load = async (orderId: string) => {
  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const platformLocations = queryClient.fetchQuery(
    ["/platform/locations"],
    () => get("/platform/locations", { page: 1 })
  );

  const productsOrder = queryClient.fetchQuery(
    ["/retailproducts/orders/{orderId}"],
    async () => {
      return await get("/retailproducts/orders/{orderId}", {
        orderId,
      });
    }
  );

  const retailProductRefunds = queryClient.fetchQuery(
    ["/retailproducts/{platformId}/refund-support-details/{orderId}"],
    async () => {
      return await get(
        "/retailproducts/{platformId}/refund-support-details/{orderId}",
        {
          platformId: (await platform).id,
          orderId,
        }
      );
    }
  );

  const storefrontSettings = await queryClient
    .fetchQuery(["/retailproducts/storefront-settings"], () =>
      get("/retailproducts/storefront-settings")
    )
    .catch(() => ({ supportEmailAddresses: [], refundEligibilityDays: 30 }));

  return waitObj({
    platform,
    platformLocations,
    productsOrder,
    orderId,
    retailProductRefunds,
    storefrontSettings,
  });
};

export type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletProductOrderDetailsLoader: LoaderFunction = async ({
  params,
}) => {
  const { orderId } = params;
  if (!orderId) {
    throw new Error("Order ID is required");
  }
  return await load(orderId);
};

const WalletProductOrderDetailsPage = () => {
  const {
    productsOrder,
    platformLocations,
    retailProductRefunds,
    storefrontSettings,
  } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderType = searchParams.get("type");
  const itemId = searchParams.get("itemId");

  const pickupRedeemedDates = productsOrder.lineItemsForPickup
    .map((item) => item.lastRedeemedOnUtc)
    .filter((date): date is string => date !== null)
    .map((date) => new Date(date).getTime());

  const shippingDeliveryDates = productsOrder.lineItemsForShipping
    .map((item) => item.estimatedDeliveryDateAtUtc)
    .filter((date): date is string => date !== null)
    .map((date) => new Date(date).getTime());

  const allRelevantDates = [...pickupRedeemedDates, ...shippingDeliveryDates];

  const eligibilityEndDate =
    allRelevantDates.length > 0
      ? new Date(Math.max(...allRelevantDates))
      : new Date(productsOrder.createdOnUtc);

  const refundEligibilityDays = storefrontSettings?.refundEligibilityDays ?? 30;

  const refundEligibilityEndDate = new Date(eligibilityEndDate);
  refundEligibilityEndDate.setDate(
    refundEligibilityEndDate.getDate() + refundEligibilityDays
  );

  const isPastOrder =
    orderType === "past" || new Date() > refundEligibilityEndDate;

  if (!productsOrder) {
    return (
      <div className="p-4">
        <div className="text-center">
          <h1 className="mb-2 text-xl font-semibold text-gray-900">
            Order Not Found
          </h1>
          <p className="mb-4 text-gray-500">
            The order you&apos;re looking for could not be found.
          </p>
          <Button onClick={() => navigate("/wallet/products")} style="primary">
            Back to Products
          </Button>
        </div>
      </div>
    );
  }

  const locations =
    (platformLocations.items as Models<"PlatformLocationModel1">[]) || [];
  const location = locations.find((loc) => loc.id === productsOrder.locationId);

  let pickupItems = productsOrder.lineItemsForPickup;
  let shippingItems = productsOrder.lineItemsForShipping;

  if (isPastOrder && itemId) {
    pickupItems = pickupItems.filter((item) => item.packageId === itemId);
    shippingItems = shippingItems.filter((item) => item.packageId === itemId);
  }

  const pickupProgress = getPickupProgress(pickupItems);
  const deliveryProgress = getDeliveryProgress(shippingItems);

  const hasPendingPickupItems = pickupItems.some(
    (item) => item.status !== OrderPickupStatus.PickedUp
  );
  const hasPendingShippingItems = shippingItems.some(
    (item) => item.status !== OrderDeliveryStatus.Delivered
  );
  const hasPendingItems = hasPendingPickupItems || hasPendingShippingItems;

  const isEligibilityExpired = hasPendingItems
    ? false
    : new Date() > refundEligibilityEndDate;

  const refundDescription = isEligibilityExpired
    ? `Return window closed on ${formatToSimpleDate(
        refundEligibilityEndDate.toISOString()
      )}`
    : `Refunds/Eligible through ${formatToSimpleDate(
        refundEligibilityEndDate.toISOString()
      )}`;

  return (
    <div className="flex h-full flex-col space-y-4 bg-two">
      <TopNav
        startIconSVG={ChevronLeft}
        onStartIconClick={() => {
          navigate("/wallet/products");
        }}
      >
        <h1 className="text-title">Products</h1>
      </TopNav>

      <RetailProductOrderHeader
        order={productsOrder}
        retailProductRefunds={retailProductRefunds}
      />

      {pickupItems.length > 0 && (
        <div className="px-4">
          <div className="rounded-lg bg-white p-4 shadow-subtle">
            <RetailProductPickupSection
              context="product-details"
              pickupItems={pickupItems}
              pickupProgress={pickupProgress}
              locationName={location?.name}
              orderType={orderType || "upcoming"}
              orderId={productsOrder.orderID}
            />
          </div>
        </div>
      )}

      {shippingItems.length > 0 && (
        <div className="px-4">
          <div className="rounded-lg bg-white p-4 shadow-subtle">
            <RetailProductDeliverySection
              context="product-details"
              deliveryItems={shippingItems}
              deliveryProgress={deliveryProgress}
              orderType={orderType || "upcoming"}
            />
          </div>
        </div>
      )}

      <RetailProductOrderActions
        cancelEligibilityExpired={isEligibilityExpired}
        refundDescription={refundDescription}
        orderId={productsOrder.orderID}
        hasPickupItems={pickupItems.length > 0}
        hasShippingItems={shippingItems.length > 0}
      />
    </div>
  );
};

export default WalletProductOrderDetailsPage;
